// src/pages/Admin/Components/CustomersPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  ChevronRight,
  ChevronLeft,
  User,
  Phone,
  Calendar,
  X,
  Loader2,
  Edit,
  Trash2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { adminGetUsers, adminUpdateUser, adminDeleteUser } from '../../../services/adminServices';

const CustomersPage = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth) || {};
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    is_staff: false,
    is_active: true,
  });

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const itemsPerPage = 10;
  const searchTimeoutRef = useRef(null);
  const searchDelayTimeoutRef = useRef(null);

  const fetchUsers = () => {
    if (!userInfo?.access) return;
    setLoading(true);
    adminGetUsers({ userInfo, dispatch })
      .then(res => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'خطا در دریافت کاربران');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [dispatch, userInfo]);

  const handleDelete = (userId) => {
    if (!window.confirm('آیا از حذف این کاربر مطمئن هستید؟ این عمل غیرقابل بازگشت است.')) return;
    setActionLoading(userId);
    adminDeleteUser(userId, { userInfo, dispatch })
      .then(() => {
        fetchUsers();
      })
      .catch(err => {
        alert('خطا در حذف کاربر');
        console.error(err);
      })
      .finally(() => setActionLoading(null));
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      is_staff: user.is_staff || false,
      is_active: user.is_active !== undefined ? user.is_active : true,
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm({ first_name: '', last_name: '', phone: '', is_staff: false, is_active: true });
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveEdit = () => {
    if (!editForm.phone || editForm.phone.length !== 11 || !/^\d+$/.test(editForm.phone)) {
      alert('شماره تلفن باید ۱۱ رقم باشد.');
      return;
    }
    setActionLoading(editingUser);
    adminUpdateUser(editingUser, {
      first_name: editForm.first_name,
      last_name: editForm.last_name,
      phone: editForm.phone,
      is_staff: editForm.is_staff,
      is_active: editForm.is_active,
    }, { userInfo, dispatch })
      .then(() => {
        fetchUsers();
        closeEditModal();
      })
      .catch(err => {
        alert('خطا در به‌روزرسانی کاربر');
        console.error(err);
      })
      .finally(() => setActionLoading(null));
  };

  const filteredUsers = users.filter(user => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return user.is_active === true;
    if (activeTab === 'inactive') return user.is_active === false;
    if (activeTab === 'admin') return user.is_staff === true;
    return true;
  });

  const handleSearch = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setCurrentPage(1);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearching(true);
      searchDelayTimeoutRef.current = setTimeout(() => {
        const results = filteredUsers.filter(u =>
          u.username?.includes(value.trim()) ||
          u.first_name?.includes(value.trim()) ||
          u.last_name?.includes(value.trim()) ||
          u.phone?.includes(value.trim())
        );
        setSearchResults(results);
        setIsSearching(false);
        setCurrentPage(1);
      }, 500);
    }, 300);
  };

  const clearSearch = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
    setCurrentPage(1);
  };

  const currentData = searchTerm.trim() ? searchResults : filteredUsers;
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = currentData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  };

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> فعال</span>;
    }
    return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200"><XCircle className="w-3.5 h-3.5 text-red-500" /> غیرفعال</span>;
  };

  const getRoleBadge = (isStaff) => {
    if (isStaff) {
      return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">مدیر</span>;
    }
    return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">کاربر</span>;
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse border-b border-gray-100">
      <td className="px-6 py-5"><div className="h-5 w-32 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5"><div className="h-5 w-40 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5"><div className="h-5 w-28 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5"><div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-5"><div className="h-5 w-24 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-5"><div className="h-16 w-16 bg-gray-200 rounded mx-auto"></div></td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded-xl"></div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">خطا: {error}</p>
          <button onClick={fetchUsers} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" dir="rtl">
      
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight mb-2">مدیریت مشتریان</h1>
          <p className="text-sm text-gray-500">لیست تمام کاربران ثبت‌شده در فروشگاه</p>
        </div>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-center md:justify-start">
          {[
            { id: 'all', label: 'همه کاربران' },
            { id: 'active', label: 'فعال' },
            { id: 'inactive', label: 'غیرفعال' },
            { id: 'admin', label: 'مدیران' },
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => { setActiveTab(filter.id); setCurrentPage(1); clearSearch(); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                activeTab === filter.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isSearching ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin" /> : <Search className="w-5 h-5 text-gray-400" />}
          </div>
          <input 
            type="text" 
            placeholder="جستجو بر اساس نام یا شماره تلفن..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-right text-sm font-bold text-gray-600 uppercase tracking-wider">
                <th className="px-6 py-4 text-right min-w-[150px]">نام</th>
                <th className="px-6 py-4 text-right min-w-[150px]">شماره تلفن</th>
                <th className="px-6 py-4 text-center min-w-[120px]">تاریخ عضویت</th>
                <th className="px-6 py-4 text-center min-w-[80px]">نقش</th>
                <th className="px-6 py-4 text-center min-w-[100px]">وضعیت</th>
                <th className="px-6 py-4 text-center min-w-[100px] bg-gray-50 border-r border-gray-200">اقدامات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isSearching ? (
                Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
              ) : currentItems.length > 0 ? (
                currentItems.map((user) => (
                  <tr key={user.id} className="hover:bg-blue-50/30 transition-colors duration-200 group">
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {user.first_name} {user.last_name}
                          </p>
                          <p className="text-xs text-gray-400">@{user.username}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {user.first_name?.[0] || user.username?.[0] || 'U'}
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{user.phone || '—'}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm whitespace-nowrap">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(user.date_joined)}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-center">
                      {getRoleBadge(user.is_staff)}
                    </td>

                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(user.is_active)}
                    </td>

                    <td className="px-6 py-4 text-center border-r border-gray-200 bg-gray-50/30">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(user)}
                          disabled={actionLoading === user.id}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="ویرایش کاربر"
                        >
                          {actionLoading === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Edit className="w-4 h-4" />
                          )}
                        </button>

                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={actionLoading === user.id}
                          className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="حذف کاربر"
                        >
                          {actionLoading === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center py-12 text-gray-500 font-medium">کاربری با این مشخصات یافت نشد.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {!isSearching && totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 gap-4">
            <div className="text-sm text-gray-600 font-medium order-2 sm:order-1">
              نمایش <span className="font-bold text-gray-900">{startIndex + 1}</span> تا <span className="font-bold text-gray-900">{endIndex}</span> عدد از <span className="font-bold text-gray-900">{totalItems}</span> کاربر
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
              <button 
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((p) => (
                <button 
                  key={p}
                  onClick={() => goToPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded text-sm font-bold transition-colors ${
                    currentPage === p 
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-200' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}>
                  {p}
                </button>
              ))}
              <button 
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ===== مودال ویرایش کاربر ===== */}
      {editingUser !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">ویرایش کاربر</h2>
              <button onClick={closeEditModal} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">نام</label>
                <input
                  type="text"
                  name="first_name"
                  value={editForm.first_name}
                  onChange={handleEditChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="نام"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">نام خانوادگی</label>
                <input
                  type="text"
                  name="last_name"
                  value={editForm.last_name}
                  onChange={handleEditChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="نام خانوادگی"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">شماره تلفن</label>
                <input
                  type="tel"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                  maxLength="11"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_staff"
                    checked={editForm.is_staff}
                    onChange={handleEditChange}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  مدیر
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={editForm.is_active}
                    onChange={handleEditChange}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  فعال
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                انصراف
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={actionLoading === editingUser}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {actionLoading === editingUser ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                ذخیره تغییرات
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;